// src/components/shared_components/AnnouncementDialog.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAnnouncement } from '../../hooks/useAnnouncement';

/**
 * One-time announcement shown to logged-in users. Copy comes from
 * constants/announcements.js; the seen/dismissed state is handled by
 * useAnnouncement, which records it on the user's Firestore document.
 */
const AnnouncementDialog = () => {
  const navigate = useNavigate();
  const { announcement, open, dismiss } = useAnnouncement();

  if (!announcement) {
    return null;
  }

  const handleCta = async () => {
    await dismiss();
    if (announcement.ctaPath) {
      navigate(announcement.ctaPath);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => dismiss()}
      maxWidth="sm"
      fullWidth
      aria-labelledby="announcement-title"
      PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
          color: 'common.white',
          px: 3,
          py: 2.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          component="img"
          src="/images/edc_logo_circle.png"
          alt="Education Data Collective"
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            bgcolor: 'common.white',
            p: 0.5,
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', opacity: 0.85, letterSpacing: '0.08em', lineHeight: 1.6 }}
          >
            {announcement.eyebrow}
          </Typography>
          <Typography id="announcement-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {announcement.title}
          </Typography>
        </Box>
        <IconButton
          onClick={() => dismiss()}
          size="small"
          aria-label="Dismiss announcement"
          sx={{ color: 'common.white', mt: -0.5, mr: -1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 1 }}>
        {announcement.body.map((paragraph, index) => (
          <Typography
            key={paragraph}
            variant="body1"
            color="text.secondary"
            sx={{ mb: index === announcement.body.length - 1 ? 0 : 1.5 }}
          >
            {paragraph}
          </Typography>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={() => dismiss()} sx={{ color: 'text.secondary' }}>
          {announcement.dismissLabel}
        </Button>
        {announcement.ctaPath && (
          <Button onClick={handleCta} variant="contained" disableElevation>
            {announcement.ctaLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AnnouncementDialog;
